import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { Modal, Button, Form } from "react-bootstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import pdfIcon from "/Logo_pdf.webp";


const FamilyHistory = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [groupId, setGroupId] = useState("");
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberName, setMemberName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [editData, setEditData] = useState({
    name: "",
    relation: "",
    plan: "",
    status: "",
    due: "",
    maturity: "",
    loan: "",
  });

  const [memberQuery, setMemberQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);  
  const [search, setSearch] = useState({
    primaryHolder: "",
    groupId: "",
    policyNumber: "",
  })

  const pdfRef = useRef(null);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ✅ Calculate totals dynamically
  const totals = React.useMemo(() => {
    return familyMembers.reduce(
      (acc, member) => {
        acc.totalDue += parseFloat(member.due) || 0;
        acc.totalMaturity += parseFloat(member.maturity) || 0;
        acc.totalLoan += parseFloat(member.loan) || 0;
        return acc;
      },
      { totalDue: 0, totalMaturity: 0, totalLoan: 0 }
    );
  }, [familyMembers]);

  // ✅ Generate PDF with LOGO ON RIGHT, POLICY NUMBER, CENTERED TABLE, TOTAL (INR)
  const generatePDF = () => {
    if (familyMembers.length === 0) {
      alert("No data to export!");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // ✅ ADD COMPANY LOGO - RIGHT SIDE
    const logoWidth = 25;
    const logoHeight = 25;
    const logoX = pageWidth - logoWidth - 14; // Right side with margin
    const logoY = 8;
    
    // Placeholder box for logo (replace with your actual logo)
    doc.addImage(pdfIcon, 'PNG', logoX, logoY, logoWidth, logoHeight);

    
    // Add title (centered)
    doc.setFontSize(20);
    doc.setTextColor(8, 145, 178);
    doc.text("Family History Report", 105, 20, { align: "center" });
    
    // ✅ Add group info WITH POLICY NUMBER
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    
    // Left column
    doc.text(`Group ID: ${search.groupId || "N/A"}`, 14, 42);
    doc.text(`Primary Holder: ${search.primaryHolder || "N/A"}`, 14, 49);
    
    // Right column
    doc.text(`Policy Number: ${search.policyNumber || "N/A"}`, 120, 42);
    doc.text(`Generated on: ${new Date().toLocaleDateString("en-IN")}`, 120, 49);
    
    // Prepare table data
    const tableData = familyMembers.map((member, idx) => [
      idx + 1,
      member.name || "NA",
      member.relation || "NA",
      member.plan || "NA",
      member.status || "NA",
      member.due || "NA",
      member.maturity || "NA",
      member.loan || "NA",
    ]);

    // Add total row with TOTAL (INR) label
    tableData.push([
      { 
        content: "TOTAL (INR)", 
        colSpan: 5, 
        styles: { 
          fontStyle: "bold", 
          fillColor: [236, 254, 255], 
          textColor: [8, 145, 178], 
          halign: "right",
          fontSize: 10
        } 
      },
      { 
        content: totals.totalDue.toLocaleString('en-IN'), 
        styles: { 
          fontStyle: "bold", 
          fillColor: [236, 254, 255], 
          textColor: [12, 74, 110], 
          halign: "right",
          fontSize: 10
        } 
      },
      { 
        content: totals.totalMaturity.toLocaleString('en-IN'), 
        styles: { 
          fontStyle: "bold", 
          fillColor: [236, 254, 255], 
          textColor: [12, 74, 110], 
          halign: "right",
          fontSize: 10
        } 
      },
      { 
        content: totals.totalLoan.toLocaleString('en-IN'), 
        styles: { 
          fontStyle: "bold", 
          fillColor: [236, 254, 255], 
          textColor: [12, 74, 110], 
          halign: "right",
          fontSize: 10
        } 
      },
    ]);

    // Calculate margins for perfect centering
    const tableWidth = 180;
    const leftMargin = (pageWidth - tableWidth) / 2;

    // Add table using autoTable - PERFECTLY CENTERED
    autoTable(doc, {
      startY: 58,
      head: [["S.NO", "Family Member", "Relation", "Insurance Plan", "Status", "Due", "Maturity", "Loan"]],
      body: tableData,
      theme: "grid",
      margin: { left: leftMargin, right: leftMargin },
      tableWidth: tableWidth,
      headStyles: {
        fillColor: [8, 145, 178],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        fontSize: 9,
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        halign: "center",
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 15 },
        1: { halign: "left", cellWidth: 30 },
        2: { halign: "center", cellWidth: 20 },
        3: { halign: "left", cellWidth: 30 },
        4: { halign: "center", cellWidth: 20 },
        5: { halign: "right", cellWidth: 22 },
        6: { halign: "right", cellWidth: 22 },
        7: { halign: "right", cellWidth: 21 },
      },
      didDrawPage: (data) => {
        // Footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.setTextColor(128);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
      },
    });

    return doc;
  };

  // ✅ Handle PDF Preview
  const handlePdfPreview = () => {
    const doc = generatePDF();
    if (doc) {
      const pdfDataUri = doc.output("dataurlstring");
      setPdfPreview(pdfDataUri);
      setShowPdfModal(true);
    }
  };

  // ✅ Handle PDF Download
  const handlePdfDownload = () => {
    const doc = generatePDF();
    if (doc) {
      doc.save(`Family_History_${search.groupId || "Report"}_${new Date().toLocaleDateString("en-IN")}.pdf`);
    }
  };

  // ✅ Fixed search function
  const handleSearch = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/group/${search.groupId}`);
      const data = await res.json();
      setFamilyMembers(data.familyMembers || []);
      setSelectedMember(data.familyMembers?.[0] || null);
      
      // Set policy number if available from API response
      if (data.policyNumber) {
        setSearch(prev => ({ ...prev, policyNumber: data.policyNumber }));
      }
    } catch (err) {
      console.error("❌ Failed to fetch group data:", err);
    }
  };

  const handleExport = () => {
    alert("Exporting data...");
  };

  const handleEdit = () => {
    if (!selectedMember) {
      alert("Please select a member to edit!");
      return;
    }
    setEditData({
      name: selectedMember.name || "",
      relation: selectedMember.relation || "",
      plan: selectedMember.plan || "",
      status: selectedMember.status || "",
      due: selectedMember.due || "",
      maturity: selectedMember.maturity || "",
      loan: selectedMember.loan || "",
      _lockedPlan: !!selectedMember._lockedPlan,
      _lockedDue: !!selectedMember._lockedDue,
    });
    setShowModal(true);
  };

  const handleRowClick = (member) => {
    setSelectedMember(member);
    setEditData({
      name: member.name || "",
      relation: member.relation || "",
      plan: member.plan || "",
      status: member.status || "",
      due: member.due || "",
      maturity: member.maturity || "",
      loan: member.loan || "",
      _lockedPlan: !!member._lockedPlan,
      _lockedDue: !!member._lockedDue,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        plan: editData.plan ?? "",
        due: editData.due !== "" ? Number(editData.due) : undefined,
        maturity: editData.maturity !== "" ? Number(editData.maturity) : undefined,
        loan: editData.loan !== "" ? Number(editData.loan) : undefined,
        ...(editData.status ? { status: editData.status } : {}),
      };
      
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/members/${selectedMember._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Update failed");

      const {member} = await res.json();

      setFamilyMembers(prev => prev.map((m) => (m._id === member._id ? member : m)));
      setSelectedMember(member);
      setShowModal(false);
      alert("✅ Member updated successfully!");
    } catch (err) {
      console.error("❌ Update failed:", err);
      alert("Failed to update member.");
    }
  };

  const handleChange = (e) => {
    const { name, value} = e.target;
    setEditData(prev =>({ ...prev, [name]: value }));
  };

  const handleSelect = (suggestion) => {
    setSearch({
      primaryHolder: suggestion.primaryHolder,
      groupId: suggestion.groupId,
      policyNumber: suggestion.policyNumber || "",
    });
    setShowSuggestions(false);
    setMemberQuery("");
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (memberQuery.length < 2) { 
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/search-family-member/${groupId}`);
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch (err) {
        console.error("Error fetching suggestions", err);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };
    fetchSuggestions();
  },[memberQuery]);
    
    
  return (
    <div className="dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="main-content p-4 fh-bg">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between">
          <div>
            <h2 className="fw-bold fh-title mb-1">Family History</h2>
            <p className="fh-subtitle mb-0">
              Manage and track family member insurance policies
            </p>
          </div>
        </div>

        {/* Search Section */}
        <div className="card-soft p-4 mt-4 mb-3">
          <div className="fh-section-title">
            <span className="icon-circle">
              <i className="bi bi-search"></i>
            </span>
            <span>Search Family Members</span>
          </div>

          <div className="row g-3 mt-3">
            <div className="col-md-5">
              <label className="form-label search-label">Enter Group ID</label>
              <input
                type="text"
                className="form-control fh-input"
                placeholder="e.g. GRP-3021"
                value={search.groupId}
                onChange={(e) => { 
                   setSearch({...search, groupId: e.target.value});
                  setMemberQuery(e.target.value);
                  setGroupId(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul
                  style={{
                    listStyle: "none",
                    margin: 0,
                    padding: "5px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    background: "white",
                    position: "absolute",
                    zIndex: 2000,
                    width: "100%",
                    maxHeight: "150px",
                    overflowY: "auto",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  {suggestions.map((suggestion) => (
                    <li
                      key={suggestion._id}
                      style={{
                        padding: "8px",
                        cursor: "pointer",
                        borderBottom: "1px solid #eee",
                      }}
                      onClick={() => handleSelect(suggestion)} 
                    >
                      {suggestion.groupId} - {suggestion.primaryHolder}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="col-md-5">
              <label className="form-label search-label">Enter family member name</label>
              <input
                type="text"
                className="form-control fh-input"
                placeholder="e.g. Priya Sharma"
                value={search.primaryHolder}
                onChange={(e) => {
                  setSearch({...search, primaryHolder: e.target.value});
                  setMemberName(e.target.value)
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button type="button" className="btn btn-teal w-100" onClick={handleSearch}>
                <i className="bi bi-search me-1"></i>Search
              </button>
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap mt-3">
            <span
              className="badge text-dark"
              style={{ background: "var(--ice-100)", border: "1px solid var(--mint-300)" }}
            >
              Tip: Use Group ID to fetch all linked members
            </span>
          </div>
        </div>

        {/* Family Members Table */}
        <div className="card-soft p-4 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fh-section-title mb-0">
              <span className="icon-circle" style={{ width: 28, height: 28, boxShadow: "none" }}>
                <i className="bi bi-people-fill" />
              </span>
              <span>Family Members ({familyMembers.length})</span>
            </h6>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-teal btn-sm" onClick={handleEdit}>
                <i className="bi bi-pencil-square me-1"></i>Edit
              </button>
              {/* <button className="btn btn-outline-teal btn-sm" onClick={handleExport}>
                <i className="bi bi-download me-1"></i>Export
              </button> */}
              <button className="btn btn-outline-danger btn-sm" onClick={handlePdfPreview}>
                <i className="bi bi-file-pdf me-1"></i>PDF
              </button>
            </div>
          </div>

          <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
            <table className="table table-bordered align-middle">
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 10 }}>
                <tr>
                  <th style={{ width: "60px", textAlign: "center" }}>S.NO</th>
                  <th>FAMILY MEMBER</th>
                  <th>RELATION</th>
                  <th>INSURANCE PLAN</th>
                  <th>POLICY STATUS</th>
                  <th>DUE (₹)</th>
                  <th>MATURITY (₹)</th>
                  <th>LOAN (₹)</th>
                </tr>
              </thead>
              <tbody>
                {familyMembers.length > 0 ? (
                  <>
                    {familyMembers.map((member, idx) => (
                      <tr
                        key={idx}
                        onClick={() => handleRowClick(member)}
                        className={selectedMember?._id === member._id ? "row-highlight" : ""}
                        style={{ 
                          cursor: "pointer",
                          transition: "background-color 0.2s ease"
                        }}
                        onMouseEnter={(e) => {
                          if (selectedMember?._id !== member._id) {
                            e.currentTarget.style.backgroundColor = "#f0f9ff";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedMember?._id !== member._id) {
                            e.currentTarget.style.backgroundColor = "";
                          }
                        }}
                      >
                        <td style={{ textAlign: "center", fontWeight: "600", color: "#64748b" }}>
                          {idx + 1}
                        </td>
                        <td>{member.name || "NA"}</td>
                        <td>{member.relation || "NA"}</td>
                        <td>{member.plan || "NA"}</td>
                        <td>
                          <span className={member.status === "Active" ? "badge badge-soft-active" : "badge bg-secondary"}>
                            {member.status || "NA"}
                          </span>
                        </td>
                        <td style={{ fontWeight: "500", color: "#0891b2" }}>{member.due || "NA"}</td>
                        <td style={{ fontWeight: "500", color: "#0891b2" }}>{member.maturity || "NA"}</td>
                        <td style={{ fontWeight: "500", color: "#0891b2" }}>{member.loan || "NA"}</td>
                      </tr>
                    ))}

                    <tr style={{ 
                      background: "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)", 
                      borderTop: "3px solid #0891b2",
                      position: "sticky",
                      bottom: 0,
                      zIndex: 5,
                      boxShadow: "0 -4px 10px rgba(0,0,0,0.05)",
                      pointerEvents: "none"
                    }}>
                      <td colSpan="5" style={{ 
                        padding: "15px 10px", 
                        textAlign: "right", 
                        fontWeight: "bold", 
                        color: "#0891b2",
                        fontSize: "16px"
                      }}>
                        <i className="bi bi-calculator me-2"></i>
                        TOTAL
                      </td>
                      <td style={{ 
                        padding: "15px 10px", 
                        fontWeight: "bold", 
                        color: "#0c4a6e",
                        fontSize: "16px"
                      }}>
                        ₹ {totals.totalDue.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                      <td style={{ 
                        padding: "15px 10px", 
                        fontWeight: "bold", 
                        color: "#0c4a6e",
                        fontSize: "16px"
                      }}>
                        ₹ {totals.totalMaturity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                      <td style={{ 
                        padding: "15px 10px", 
                        fontWeight: "bold", 
                        color: "#0c4a6e",
                        fontSize: "16px"
                      }}>
                        ₹ {totals.totalLoan.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered className="edit-family-modal">
          <Modal.Header closeButton className="edit-modal-header">
            <Modal.Title className="edit-modal-title">
              <i className="bi bi-person-gear me-2"></i>
              Edit Family Member
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="edit-modal-body">
            <Form>
              <div className="form-section">
                <div className="section-header">
                  <i className="bi bi-person-circle"></i>
                  <span>Personal Information</span>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <Form.Label className="form-label">Family Member Name</Form.Label>
                      <div className="input-wrapper disabled">
                        <i className="bi bi-person input-icon"></i>
                        <Form.Control 
                          className="form-input" 
                          type="text" 
                          name="name" 
                          value={editData.name} 
                          disabled 
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <Form.Label className="form-label">Relation</Form.Label>
                      <div className="input-wrapper disabled">
                        <i className="bi bi-heart input-icon"></i>
                        <Form.Control 
                          className="form-input" 
                          type="text" 
                          name="relation" 
                          value={editData.relation} 
                          disabled 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <i className="bi bi-shield-check"></i>
                  <span>Policy Information</span>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <Form.Label className="form-label">Insurance Plan</Form.Label>
                      <div className={`input-wrapper ${editData._lockedPlan ? 'disabled' : ''}`}>
                        <i className="bi bi-clipboard-data input-icon"></i>
                        <Form.Control
                          className="form-input"
                          type="text"
                          name="plan"
                          value={editData.plan}
                          onChange={handleChange}
                          disabled={!!editData._lockedPlan}
                          placeholder="Enter insurance plan"
                        />
                      </div>
                      {editData._lockedPlan && (
                        <small className="helper-text">
                          <i className="bi bi-info-circle me-1"></i>
                          Auto-filled from policy
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <Form.Label className="form-label">Policy Status</Form.Label>
                      <div className="input-wrapper disabled">
                        <i className="bi bi-check-circle input-icon"></i>
                        <Form.Control 
                          className="form-input" 
                          type="text" 
                          name="status" 
                          value={editData.status} 
                          disabled 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <i className="bi bi-currency-rupee"></i>
                  <span>Financial Information</span>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <Form.Label className="form-label">Due Amount (₹)</Form.Label>
                      <div className={`input-wrapper ${editData._lockedDue ? 'disabled' : ''}`}>
                        <i className="bi bi-calendar-x input-icon"></i>
                        <Form.Control
                          className="form-input"
                          type="number"
                          name="due"
                          value={editData.due}
                          onChange={handleChange}
                          disabled={!!editData._lockedDue}
                          placeholder="0.00"
                        />
                      </div>
                      {editData._lockedDue && (
                        <small className="helper-text">
                          <i className="bi bi-info-circle me-1"></i>
                          Auto-filled from policy
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <Form.Label className="form-label">Maturity Amount (₹)</Form.Label>
                      <div className="input-wrapper">
                        <i className="bi bi-piggy-bank input-icon"></i>
                        <Form.Control
                          className="form-input"
                          type="number"
                          name="maturity"
                          value={editData.maturity}
                          onChange={handleChange}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <Form.Label className="form-label">Loan Amount (₹)</Form.Label>
                      <div className="input-wrapper">
                        <i className="bi bi-cash input-icon"></i>
                        <Form.Control
                          className="form-input"
                          type="number"
                          name="loan"
                          value={editData.loan}
                          onChange={handleChange}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer className="edit-modal-footer">
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="cancel-btn">
              <i className="bi bi-x-lg me-2"></i>
              Cancel
            </Button>
            <Button onClick={handleSave} className="save-btn">
              <i className="bi bi-check-lg me-2"></i>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>

        {/* PDF Preview Modal */}
        <Modal 
          show={showPdfModal} 
          onHide={() => setShowPdfModal(false)} 
          size="xl" 
          centered
          className="pdf-preview-modal"
        >
          <Modal.Header closeButton style={{ backgroundColor: "#0891b2", color: "white" }}>
            <Modal.Title>
              <i className="bi bi-file-pdf me-2"></i>
              PDF Preview - Family History Report
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 0, height: "70vh" }}>
            {pdfPreview && (
              <iframe
                src={pdfPreview}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="PDF Preview"
              />
            )}
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: "#f8f9fa" }}>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowPdfModal(false)}
              style={{ padding: "10px 20px" }}
            >
              <i className="bi bi-x-circle me-2"></i>
              Close
            </Button>
            <Button 
              onClick={handlePdfDownload}
              style={{ 
                backgroundColor: "#dc3545", 
                border: "none",
                padding: "10px 20px"
              }}
            >
              <i className="bi bi-download me-2"></i>
              Download PDF
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default FamilyHistory;
